import React, { useState } from "react";
import {
  Box, Typography, IconButton, Tooltip, Popover, TextField,
  InputAdornment, List, ListItem, ListItemButton, Skeleton,
} from "@mui/material";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import { getAuthToken } from "../../../../../utils/getAuthToken";
import { SERVER_DOMAIN } from "../constants";
import { skeletonSx } from "./LabeledField";

export default function AssigneePicker({ onSelect }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    if (teams.length || members.length) return;
    setLoading(true);
    try {
      const token = await getAuthToken();
      const [teamsRes, membersRes] = await Promise.all([
        fetch(`${SERVER_DOMAIN}/api/teams/get`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${SERVER_DOMAIN}/api/teammembers/getactive`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const teamsData = await teamsRes.json();
      const membersData = await membersRes.json();
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setMembers(Array.isArray(membersData) ? membersData : []);
    } catch (err) { console.error("Failed to load assignees", err); }
    finally { setLoading(false); }
  };

  const handleClose = () => { setAnchorEl(null); setSearch(""); };
  const handleSelect = (id, name, type) => {
    onSelect({ assigneeId: id, assigneeName: name, assigneeType: type });
    handleClose();
  };

  const q = search.toLowerCase();
  const filteredTeams = teams.filter((t) => (t.teamName || "").toLowerCase().includes(q));
  const filteredMembers = members.filter((m) => (m.name || "").toLowerCase().includes(q));

  return (
    <>
      <Tooltip title="Pick team or member">
        <IconButton size="small" onClick={handleOpen} color="primary">
          <AlternateEmailIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open} anchorEl={anchorEl} onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { width: 280, maxHeight: 360 } }}
      >
        <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <TextField
            size="small" fullWidth placeholder="Search…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            autoFocus
          />
        </Box>
        {loading ? (
          // Was two bare full-width bars, no relation to the real row
          // shape below (Rahim, 2026-09-04 audit: "generic bars, doesn't
          // match"). Now icon-circle + name-bar per row, same px/py as the
          // real ListItemButton rows.
          <Box sx={{ py: 0.5 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.5 }}>
                <Skeleton variant="circular" width={18} height={18} sx={skeletonSx} />
                <Skeleton width={`${60 - i * 8}%`} height={18} sx={skeletonSx} />
              </Box>
            ))}
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: "auto", maxHeight: 280 }}>
            {filteredTeams.length > 0 && (
              <>
                <ListItem sx={{ py: 0.25, px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>TEAMS</Typography>
                </ListItem>
                {filteredTeams.map((t) => (
                  <ListItemButton key={t._id} onClick={() => handleSelect(t._id, t.teamName, "team")} sx={{ px: 1.5, py: 0.5 }}>
                    <GroupIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.primary">{t.teamName}</Typography>
                  </ListItemButton>
                ))}
              </>
            )}
            {filteredMembers.length > 0 && (
              <>
                <ListItem sx={{ py: 0.25, px: 1.5, mt: filteredTeams.length ? 0.5 : 0 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>MEMBERS</Typography>
                </ListItem>
                {filteredMembers.map((m) => (
                  <ListItemButton key={m._id} onClick={() => handleSelect(m._id, m.name, "member")} sx={{ px: 1.5, py: 0.5 }}>
                    <PersonIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography variant="body2" color="text.primary">{m.name}</Typography>
                  </ListItemButton>
                ))}
              </>
            )}
            {filteredTeams.length === 0 && filteredMembers.length === 0 && (
              <ListItem sx={{ px: 1.5 }}>
                <Typography variant="body2" color="text.secondary">No results</Typography>
              </ListItem>
            )}
          </List>
        )}
      </Popover>
    </>
  );
}
